/**
 * Channel Editor Component
 * Drawer for viewing and editing channel details
 */

import {
  Stack,
  Title,
  P,
  Card,
  useToast,
  useActions,
  Icon,
  useTheme,
  Tag,
  Button,
  H4,
  IconTag
} from "@introist/react-foundation/v2";
import { DrawerHeader } from "../../foundation/templates/Drawer/DrawerHeader";
import { useMemo } from "react";
import { useChannels } from "../hooks/use-channels";
import { useAgents } from "../hooks/use-agents";
import { channelsApi } from "../api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getPlatformIcon } from "./PlatformIcon";
import { getAppIcon } from "./AppIcon";
import { useNavigate } from "react-router-dom";

interface ChannelEditorProps {
  channelId: string;
  onClose: () => void;
}

export const ChannelEditor = ({ channelId, onClose }: ChannelEditorProps) => {
  const { data: channels = [] } = useChannels();
  const { data: agents = [] } = useAgents({ channelId });
  const queryClient = useQueryClient();
  const toast = useToast();
  const { theme } = useTheme();
  const { onConfirmAction } = useActions();
  const navigate = useNavigate();

  const channel = useMemo(() => channels.find(c => c.id === channelId), [channels, channelId]);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!channel) throw new Error("Channel not found");
      return channelsApi.delete(channel.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["ai-studio", "channels"] });
      toast.success("Channel deleted successfully");
      onClose();
    },
    onError: (error: any) => {
      toast.error(`Failed to delete: ${error.message}`);
    }
  });

  const handleAuthorize = async () => {
    if (!channel) return;
    try {
      await channelsApi.initiateOAuth(channel.id);
      toast.success("Authorization opened in new tab");
    } catch (error: any) {
      toast.error(`Failed to start authorization: ${error.message}`);
    }
  };

  const handleDelete = onConfirmAction(
    async () => {
      await deleteMutation.mutateAsync();
    },
    {
      title: "Delete channel",
      description: `Are you sure you want to delete the ${channel?.name} channel? This action cannot be undone.`,
      confirmTitle: "Delete"
    }
  );

  if (!channel) {
    return (
      <Stack vertical style={{ padding: "var(--spacing-large)" }}>
        <DrawerHeader title="Channel Not Found" onClose={onClose} />
        <P>This channel could not be found.</P>
      </Stack>
    );
  }

  return (
    <Stack vertical style={{ padding: "var(--spacing-xLarge)", gap: "var(--spacing-xLarge)" }}>
      <DrawerHeader
        title={
          <Stack>
            {getAppIcon(channel.app_id, 32)} <H4>{channel.name}</H4>
          </Stack>
        }
        onClose={onClose}
        actions={[
          {
            key: "delete",
            title: "Delete",
            onClick: handleDelete,
            startAdornment: <Icon name="trash" color={theme.palette.danger.default} />,
            titleStyles: { color: "var(--palette-danger-default)" }
          }
        ]}
      />

      <Tag colorVariant="outlined" startAdornment={getPlatformIcon(channel.platform)}>
        {channel.platform}
      </Tag>

      {/* Status Card */}
      <Card>
        <Stack
          style={{
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Stack vertical style={{ gap: "var(--spacing-xxSmall)", flex: 1 }}>
            <Title variant="bold">Status</Title>
            <Title small style={{ color: "var(--palette-foreground-subdued)" }}>
              {channel.status === "active" && "Connected and active"}
              {channel.status === "pending_oauth" && "OAuth configuration required"}
              {channel.status === "error" && "Connection error"}
            </Title>
          </Stack>
          {channel.status === "pending_oauth" && (
            <Button variant="outlined" size="small" onClickWithLoading={handleAuthorize}>
              Authorize
            </Button>
          )}
          {channel.status === "active" && (
            <IconTag color={theme.palette.success} name="checkCircle" />
          )}
        </Stack>
      </Card>

      {/* Connected Agents */}
      <Card>
        <Stack vertical style={{ gap: "var(--spacing-medium)" }}>
          <Stack style={{ justifyContent: "space-between", alignItems: "center" }}>
            <Title variant="bold">Connected Agents</Title>
          </Stack>
          {agents.length === 0 ? (
            <Title style={{ color: "var(--palette-foreground-subdued)" }}>
              No agents are using this channel
            </Title>
          ) : (
            <Stack vertical style={{ gap: "var(--spacing-xSmall)" }}>
              {agents.map(agent => (
                <Card
                  key={agent.id}
                  onClick={() => navigate(`/aistudio/agents/${agent.id}`)}
                  style={{ width: "100%", padding: "var(--spacing-small)" }}
                >
                  <Stack
                    style={{
                      padding: "var(--spacing-small)",
                      borderRadius: "var(--radius-medium)",
                      background: "var(--palette-background-subdued)",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Stack style={{ alignItems: "center" }}>
                      <Icon name="robot" />
                      <Title variant="bold">{agent.name}</Title>
                    </Stack>
                    <Icon name="chevronRight" dimmed />
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </Stack>
      </Card>

      {/* Channel Details */}
      <Card>
        <Stack vertical style={{ gap: "var(--spacing-medium)" }}>
          <Title variant="bold">Details</Title>
          <Stack vertical style={{ gap: "var(--spacing-small)" }}>
            <Stack style={{ justifyContent: "space-between" }}>
              <Title small style={{ color: "var(--palette-foreground-subdued)" }}>
                Channel ID
              </Title>
              <Title small>{channel.id}</Title>
            </Stack>
            <Stack style={{ justifyContent: "space-between" }}>
              <Title small style={{ color: "var(--palette-foreground-subdued)" }}>
                Created
              </Title>
              <Title small>{new Date(channel.created_at).toLocaleDateString()}</Title>
            </Stack>
            <Stack style={{ justifyContent: "space-between" }}>
              <Title small style={{ color: "var(--palette-foreground-subdued)" }}>
                Last Updated
              </Title>
              <Title small>{new Date(channel.updated_at).toLocaleDateString()}</Title>
            </Stack>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
};
